import { Check } from 'lucide-react';
import { intervalLabel, type SubscriptionPlan } from '@features/plans/model/plan.types';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';
import { formatDate } from '@shared/lib/format-date';
import { formatPrice } from '@shared/lib/format-price';

interface PlanDetailDialogProps {
  /** The plan being inspected, or null when the dialog is closed. */
  plan: SubscriptionPlan | null;
  onClose: () => void;
}

/**
 * One plan, laid out the way a plan is actually read: what it costs, what you
 * get for it, then how it is performing.
 *
 * Built as a pricing card rather than a field list. The price is the plan's
 * headline fact, so it is the headline here too — it used to be small subtitle
 * text repeated again in a spec grid below, which gave the most important
 * number the least weight and said it twice.
 *
 * Read-only: plans are authored in the product.
 */
export function PlanDetailDialog({ plan, onClose }: PlanDetailDialogProps) {
  const price = plan ? formatPrice(plan.price, plan.currency) : null;
  const was = plan ? formatPrice(plan.originalPrice, plan.currency) : null;
  const interval = plan ? intervalLabel(plan.interval) : '';
  const isOneOff = interval === 'One-time';
  // A complimentary plan says so. "0$ one-time" is technically the price and
  // tells the reader nothing — and free versus paid is the distinction the rest
  // of the admin is built on.
  const isFree = plan?.price === 0;

  return (
    <Modal
      open={plan !== null}
      onClose={onClose}
      title={plan?.name ?? 'Plan'}
      className="max-w-lg"
      footer={
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      {plan && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
            <div>
              <p className="flex items-baseline gap-1.5">
                {/* Proportional figures: a headline number, not a column. */}
                <span className="text-3xl font-semibold tracking-tight text-slate-900">
                  {isFree ? 'Free' : (price ?? 'No price')}
                </span>
                {price && !isFree && (
                  <span className="text-sm text-slate-500">
                    {isOneOff ? 'one-time' : `per ${plan.interval?.toLowerCase() ?? 'period'}`}
                  </span>
                )}
              </p>

              {/* Only when it is genuinely a discount, not when the two match. */}
              {was && was !== price && (
                <p className="mt-0.5 text-xs text-slate-400">
                  was <span className="line-through">{was}</span>
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <Badge tone={plan.isActive ? 'positive' : 'neutral'}>
                {plan.isActive ? 'Live' : 'Retired'}
              </Badge>
              {plan.isPopular && <Badge tone="neutral">Popular</Badge>}
              {/* The badge is marketing copy about the price, so it sits with
                  the price rather than under a heading of its own. */}
              {plan.badge && <Badge tone="neutral">{plan.badge}</Badge>}
            </div>
          </div>

          {plan.description && (
            <p className="text-sm leading-relaxed text-slate-600">{plan.description}</p>
          )}

          <section>
            <SectionLabel>What's included</SectionLabel>

            {plan.features.length === 0 ? (
              <p className="text-sm text-slate-400">This plan lists no features.</p>
            ) : (
              // Ordered, because this is the order the pricing page renders them.
              <ol className="space-y-2">
                {plan.features.map((feature, index) => (
                  <li
                    key={`${index}-${feature}`}
                    className="flex items-start gap-2.5 text-sm text-slate-700"
                  >
                    <span
                      aria-hidden
                      className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-periscope-100"
                    >
                      <Check className="h-2.5 w-2.5 text-periscope-700" strokeWidth={3.5} />
                    </span>
                    {feature}
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section>
            <SectionLabel>How it is doing</SectionLabel>

            {/* Three figures worth comparing, so they get equal weight side by
                side rather than becoming rows in a field list. */}
            <div className="grid grid-cols-3 gap-2">
              <Figure label="Active now" value={String(plan.activeSubscriberCount)} />
              <Figure label="All time" value={String(plan.subscriberCount)} />
              <Figure
                label="Monthly revenue"
                value={
                  plan.monthlyRevenue > 0
                    ? (formatPrice(Math.round(plan.monthlyRevenue), plan.currency) ?? '—')
                    : '—'
                }
              />
            </div>
          </section>

          {/* Bookkeeping, not something anyone opens this to find. */}
          <p className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Added {formatDate(plan.createdAt)} · billed {interval.toLowerCase()} ·{' '}
            {plan.currency ?? 'no currency'} · position {plan.sortOrder}
          </p>
        </div>
      )}
    </Modal>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
      {children}
    </p>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-center">
      <p className="text-lg font-semibold tracking-tight text-slate-900">{value}</p>
      <p className="mt-0.5 text-[11px] leading-tight text-slate-500">{label}</p>
    </div>
  );
}
